import styled, { ThemeProvider } from 'styled-components';
import compactNameRenderer from './components/CompactNameRenderer';
import standardProductNameRenderer from './components/StandardProductNameRenderer';
import { Brand, Snippet } from './shared/styles';
import type { IProductName } from './shared/types';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: ${({ theme }) => theme.Spacing2};
    line-height: normal;
`;

const ProductNameContainer = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: ${({ theme }) => theme.Spacing6};
    line-height: normal;
`;

export default function ProductName(props: IProductName): JSX.Element {
    const {
        productName,
        snippet,
        size = 'md',
        nameRenderer = standardProductNameRenderer,
        as,
        customTheme,
        strongProductTitle = true,
        ...restProps
    } = props;
    const { lines } = productName;
    const resolvedTheme = { ...customTheme, size };

    return (
        <Container {...restProps}>
            <ThemeProvider theme={resolvedTheme as any}>
                <ProductNameContainer>
                    {lines.map((line) => nameRenderer(line, strongProductTitle, size, as))}
                </ProductNameContainer>
                {snippet && <Snippet size="S">{snippet}</Snippet>}
            </ThemeProvider>
        </Container>
    );
}

export function ProductNameCompact(props: IProductName) {
    const {
        productName,
        snippet,
        size = 'md',
        nameRenderer = compactNameRenderer,
        strongProductTitle = true,
        ...restProps
    } = props;
    const { lines } = productName;
    const linesToRender = [...lines];

    const brandLineIndex = linesToRender.findIndex((line) => line.type === 'Brand');
    const brandLine = linesToRender.splice(brandLineIndex, 1)[0];
    const title = linesToRender.map((line) => line.value).join(' ');

    return (
        <Container {...restProps}>
            <ThemeProvider theme={{ size } as any}>
                <ProductNameContainer>
                    {brandLine && (
                        <Brand forwardedAs="span" size="M">
                            {brandLine.value}
                        </Brand>
                    )}
                    <div title={title}>{linesToRender.map((line) => nameRenderer(line, strongProductTitle))}</div>
                </ProductNameContainer>
                {snippet && (
                    <Snippet size="S" maxVerticalLines={1}>
                        {snippet}
                    </Snippet>
                )}
            </ThemeProvider>
        </Container>
    );
}
